import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { METHOD_LABELS } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import { MOVEMENT_STATUS_LABELS } from '@/data/remittances'
import type { MovementStatus } from '@/data/remittances'
import type { MonthStatement } from '@/data/reports'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { Figure, Row } from './reportParts'

const KIND_LABELS: Record<string, string> = { dues: 'Dues', project: 'Project giving', other: 'Other income' }

/**
 * One department, one calendar month: what came in, what went out, what was sent on, and the net change in
 * the department's cash. No budget and no opening balance: budgets are set per quarter and cash is kept per
 * quarter, so those stay on the quarterly report.
 */
export function MonthStatementView({ r }: { r: MonthStatement }) {
  const c = r.cash
  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Summary">
        <Figure label="Money in" value={formatKwacha(r.income.totalNgwee)} />
        <Figure label="Spent" value={formatKwacha(r.spending.paidNgwee)} />
        <Figure label="Sent to central" value={formatKwacha(r.remittances.declaredNgwee)} />
        <Figure label="Change in your cash" value={formatKwacha(c.changeNgwee)} tone={c.changeNgwee < 0 ? 'bad' : undefined} />
      </div>

      <Card>
        <CardHeader title="Money in" />
        <CardBody>
          {r.income.byKind.length === 0 ? <p className="text-body text-muted">Nothing was collected this month.</p> : (
            <ul className="divide-y divide-line">
              {r.income.byKind.map((k) => <Row key={k.kind} left={KIND_LABELS[k.kind] ?? k.kind} right={formatKwacha(k.ngwee)} />)}
              <Row bold left="Total" right={formatKwacha(r.income.totalNgwee)} />
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Spending by category" />
        <CardBody>
          {r.spending.byCategory.length === 0 ? <p className="text-body text-muted">Nothing was paid out this month.</p> : (
            <ul className="divide-y divide-line">
              {r.spending.byCategory.map((x) => <Row key={x.category} left={x.category} right={formatKwacha(x.ngwee)} />)}
              <Row bold left="Total paid" right={formatKwacha(r.spending.paidNgwee)} />
              {r.spending.unbudgetedNgwee !== 0 && <Row left="Of which not in the budget" right={formatKwacha(r.spending.unbudgetedNgwee)} />}
            </ul>
          )}
          {r.spending.waitingNgwee !== 0 && <p className="mt-2 text-body text-muted">{formatKwacha(r.spending.waitingNgwee)} more is waiting for approval or payment.</p>}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Money sent to central treasury" />
        <CardBody>
          {r.remittances.items.length === 0 ? <p className="text-body text-muted">Nothing was sent this month.</p> : (
            <ul className="divide-y divide-line">
              {r.remittances.items.map((m, i) => (
                <Row
                  key={i}
                  left={`${formatDay(m.movedOn)} · ${METHOD_LABELS[m.method as PaymentMethod] ?? m.method}`}
                  sub={`${MOVEMENT_STATUS_LABELS[m.status as MovementStatus] ?? m.status}${m.confirmedNgwee > 0 ? `, confirmed ${formatKwacha(m.confirmedNgwee)}` : ''}`}
                  right={formatKwacha(m.declaredNgwee)}
                />
              ))}
              <Row bold left="Sent" right={formatKwacha(r.remittances.declaredNgwee)} />
              <Row bold left="Confirmed received" right={formatKwacha(r.remittances.confirmedNgwee)} />
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Change in the department's cash" />
        <CardBody>
          <ul className="divide-y divide-line">
            <Row left="Collected" right={formatKwacha(c.collectedNgwee)} />
            <Row left="Spent from department cash" right={formatKwacha(-c.spentFromCashNgwee)} />
            <Row left="Sent to central treasury" right={formatKwacha(-c.sentNgwee)} />
            {c.receivedFromCentralNgwee !== 0 && <Row left="Received from central treasury" right={formatKwacha(c.receivedFromCentralNgwee)} />}
            <Row bold left="Net change this month" right={formatKwacha(c.changeNgwee)} />
          </ul>
          <p className="mt-2 text-body text-muted">
            Cash is kept per quarter, so there is no opening or closing balance for a single month. Budgets are also set per quarter: see the quarterly report for budget against what was spent.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Dues owed right now" />
        <CardBody>
          {r.arrears.members === 0 ? <p className="text-body text-muted">Nobody is behind on dues.</p> : (
            <p className="text-ink">{r.arrears.members} {r.arrears.members === 1 ? 'member owes' : 'members owe'} {formatKwacha(r.arrears.totalNgwee)} in total.</p>
          )}
        </CardBody>
      </Card>
    </>
  )
}
